import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import ReactInputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { KTSVG } from '../../../../_metronic/helpers';
import labelKey from "../../../localization/label.json";
import TextField from '../../../../components/textFields/TextField';
import TextAreaField from '../../../../components/textFields/TextAreaField';
import { useCreateVendorMutation, useUpdateVendorMutation } from '../../../../services/VendorApi';

const PHONE_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/;

type VendorFormValues = {
    vendorId: number;
    name: string;
    disc: string;
    address: string;
    active: boolean;
    email: string;
    phone: string;
};

type Props = {
    show: boolean;
    handleClose: () => void;
    data?: any;
    refetch?: () => void;
    onVendorCreated?: (vendor: { vendorId: number; name: string }) => void;
    prefill?: Partial<VendorFormValues>;
    showInfoBanner?: boolean;
};

const initialValues: VendorFormValues = {
    vendorId: 0,
    name: '',
    disc: '',
    address: '',
    active: true,
    email: '',
    phone: '',
};

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .required(labelKey.vendorNameRequired)
        .max(255, 'Name must be no more than 255 characters'),
    disc: Yup.string()
        .trim()
        .max(500, labelKey.vendorDescriptionMax),
    address: Yup.string()
        .trim()
        .max(255, labelKey.vendorAddressMax),
    email: Yup.string()
        .trim()
        .test('email', labelKey.vendorEmailInvalid, (value) => {
            if (!value) return true;
            return Yup.string().email().isValidSync(value);
        })
        .max(255, 'Email must be no more than 255 characters'),
    phone: Yup.string()
        .trim()
        .test('phone', labelKey.vendorPhoneInvalid, (value) => {
            if (!value) return true;
            return PHONE_PATTERN.test(value);
        }),
    active: Yup.boolean(),
});

const AddUpdateVendorModal = ({
    show,
    handleClose,
    data,
    refetch,
    onVendorCreated,
    prefill,
    showInfoBanner = true,
}: Props) => {
    const [addVendor, { data: addData, isLoading, isSuccess, isError, error }] = useCreateVendorMutation();
    const [updateVendor, { data: updateData, isLoading: updateIsLoading, isSuccess: updateIsSuccess, isError: updateIsError, error: updateError }] = useUpdateVendorMutation();

    const [updateId, setUpdateId] = useState(0);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            const payload: VendorFormValues = {
                ...values,
                name: values.name.trim(),
                disc: (values.disc || '').trim(),
                address: (values.address || '').trim(),
                email: (values.email || '').trim(),
                phone: (values.phone || '').trim(),
            };

            if (updateId) {
                updateVendor(payload);
            } else {
                addVendor(payload);
            }
        },
    });

    const touchAllFields = () => {
        formik.setTouched(
            {
                name: true,
                phone: true,
                email: true,
                address: true,
                disc: true,
                active: true,
            },
            true
        );
    };

    const handleModalClose = () => {
        handleClose();
    };

    useEffect(() => {
        if (!show) {
            setUpdateId(0);
            formik.resetForm();
            return;
        }

        if (data) {
            setUpdateId(data?.vendorId);
            formik.setValues({ ...initialValues, ...data });
            return;
        }

        setUpdateId(0);
        formik.resetForm({
            values: {
                ...initialValues,
                ...(prefill || {}),
            },
        });
    }, [show, data]);

    useEffect(() => {
        if (isSuccess) {
            const responseData = (addData as any)?.message;
            const vendorId = (addData as any)?.result;
            toast.success(responseData);
            if (onVendorCreated && vendorId > 0) {
                onVendorCreated({ vendorId, name: formik.values.name.trim() });
            }
            handleClose();
            formik.resetForm();
            refetch?.();
        }
    }, [isSuccess, addData]);

    useEffect(() => {
        if (updateIsSuccess) {
            const responseData = (updateData as any)?.message;
            toast.success(responseData);
            handleClose();
            refetch?.();
        }
    }, [updateIsSuccess, updateData]);

    useEffect(() => {
        if (isError && error) {
            let errorMessage = 'Something went wrong';
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message;
            }
            toast.error(errorMessage);
        }
    }, [isError, error]);

    useEffect(() => {
        if (updateIsError && updateError) {
            let errorMessage = 'Something went wrong';
            if ('data' in updateError && updateError.data && typeof updateError.data === 'object' && 'message' in updateError.data) {
                errorMessage = (updateError.data as { message: string }).message;
            }
            toast.error(errorMessage);
        }
    }, [updateIsError, updateError]);

    const fieldClass = (field: keyof VendorFormValues) =>
        formik.touched[field] && formik.errors[field] ? 'is-invalid' : '';

    return (
        <Modal
            aria-hidden='true'
            dialogClassName='modal-dialog modal-dialog-centered ss-add-vendor-modal'
            contentClassName='ss-add-vendor-modal__content'
            show={show}
            onHide={handleModalClose}
            backdrop="static"
            centered
        >
            <div className='modal-header ss-add-vendor-modal__header'>
                <h2 className='mb-0'>{updateId ? labelKey.updateVendor : labelKey.addVendor}</h2>
                <button
                    type='button'
                    className='btn btn-sm btn-icon btn-active-color-primary'
                    onClick={handleModalClose}
                    aria-label='Close'
                >
                    <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                </button>
            </div>

            <form
                className='form w-100'
                onSubmit={(e) => {
                    e.preventDefault();
                    touchAllFields();
                    formik.handleSubmit();
                }}
            >
                <div className='modal-body ss-add-vendor-modal__body'>
                    {!updateId && showInfoBanner && (
                        <Alert variant='primary' className='ss-page-info-alert ss-add-vendor-modal__info py-2 px-3 mb-5'>
                            <div className='ss-page-info-alert__body'>
                                <p className='mb-0 fs-6'>{labelKey.addVendorInfo}</p>
                            </div>
                        </Alert>
                    )}

                    <div className='row'>
                        <div className='col-md-6 mb-5'>
                            <label className='form-label fw-bold text-dark fs-6 required'>{labelKey.name}</label>
                            <input
                                type='text'
                                autoComplete='off'
                                maxLength={255}
                                className={`form-control form-control-lg form-control-solid default-input ${fieldClass('name')}`}
                                placeholder='Enter vendor name'
                                {...formik.getFieldProps('name')}
                                value={formik.values.name || ''}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <div className='invalid-feedback d-block'>{formik.errors.name}</div>
                            )}
                        </div>

                        <div className='col-md-6 mb-5'>
                            <label className='form-label fw-bold text-dark fs-6'>{labelKey.phone}</label>
                            <ReactInputMask
                                mask="(999) 999-9999"
                                placeholder='(XXX) XXX-XXXX'
                                type='text'
                                autoComplete='off'
                                {...formik.getFieldProps('phone')}
                                value={formik.values.phone || ''}
                                className={`form-control form-control-lg form-control-solid default-input ${fieldClass('phone')}`}
                            />
                            {formik.touched.phone && formik.errors.phone && (
                                <div className='invalid-feedback d-block'>{formik.errors.phone}</div>
                            )}
                        </div>

                        <div className='col-md-12 mb-5'>
                            <TextField
                                label={labelKey.email}
                                type='email'
                                placeholder='Enter email address'
                                maxLength={255}
                                {...formik.getFieldProps('email')}
                                value={formik.values.email || ''}
                                fieldClass={fieldClass('email')}
                            />
                            {formik.touched.email && formik.errors.email && (
                                <div className='invalid-feedback d-block'>{formik.errors.email}</div>
                            )}
                        </div>

                        <div className='col-xl-12 mb-5'>
                            <TextField
                                label={labelKey.address}
                                type='text'
                                placeholder='Enter street address'
                                maxLength={255}
                                {...formik.getFieldProps('address')}
                                value={formik.values.address || ''}
                                fieldClass={fieldClass('address')}
                            />
                            {formik.touched.address && formik.errors.address && (
                                <div className='invalid-feedback d-block'>{formik.errors.address}</div>
                            )}
                        </div>

                        <div className='col-xl-12 mb-5'>
                            <TextAreaField
                                label={labelKey.description}
                                placeholder='Enter vendor description or notes'
                                maxLength={500}
                                {...formik.getFieldProps('disc')}
                                value={formik.values.disc || ''}
                                fieldClass={fieldClass('disc')}
                            />
                            {formik.touched.disc && formik.errors.disc && (
                                <div className='invalid-feedback d-block'>{formik.errors.disc}</div>
                            )}
                            <div className='form-text text-muted mt-1'>
                                {(formik.values.disc || '').length}/500
                            </div>
                        </div>

                        {updateId > 0 && (
                            <div className='col-xl-12 mb-2'>
                                <TextField
                                    rightLabel={labelKey.active}
                                    type='checkbox'
                                    checked={formik.values.active}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        formik.setFieldValue('active', e.target.checked)
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className='modal-footer ss-add-vendor-modal__footer justify-content-center gap-2'>
                    <button type='button' className='btn btn-light' onClick={handleModalClose}>
                        {labelKey.cancel}
                    </button>
                    <button
                        type='submit'
                        className='btn btn-primary'
                        disabled={isLoading || updateIsLoading}
                        onClick={() => touchAllFields()}
                    >
                        {!isLoading && !updateIsLoading && (
                            <span className='indicator-label'>{updateId ? labelKey.update : labelKey.submit}</span>
                        )}
                        {(isLoading || updateIsLoading) && (
                            <span className='indicator-progress d-flex align-items-center'>
                                {labelKey.pleaseWait}...
                                <span className='spinner-border spinner-border-sm align-middle ms-2' />
                            </span>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default AddUpdateVendorModal;
